/**
 * The seam. Three calls, and nothing else crosses it.
 *
 *   discover  GET  /api/v1/semantic/catalog
 *   values    GET  /api/v1/semantic/values
 *   execute   POST /api/v1/semantic/analysis
 *
 * The spec goes over the wire exactly as the debug panel shows it, and the
 * server validates it against the same `analysis_spec.schema.json` the design
 * calls canonical. Nothing here interprets a spec; that is the compiler's job
 * and it lives on the other side.
 */

import { api } from '../../lib/api'
import type { RootCatalog, SubjectCatalog } from './catalog'
import type { AnalysisSpec } from './spec'
import { orderSpec } from './spec'

/** One output column, described by the compiler that produced it. */
export interface ResultColumn {
  name: string
  /** breakdown (an axis) or measure (a value). */
  role: string
  label: string
  type: string
  unit?: string | null
  bin_width?: number | null
}

/**
 * One match's playing surface, as measured, in metres.
 *
 * Real dimensions vary by match and the catalog's nominal 105 x 68 is only a
 * drawing fallback, so the server sends these and the UI prefers them. A match
 * whose geometry could not be read is absent from the map, never defaulted.
 */
export interface PitchDims {
  length: number
  width: number
  /** True when these are a written-down default rather than a measurement. */
  assumed: boolean
  source: string
}

export interface AnalysisResult {
  columns: ResultColumn[]
  rows: unknown[][]
  row_count: number
  truncated: boolean
  matches: string[]
  fps: Record<string, number>
  /** Measured geometry per match; empty when the lookup failed. */
  pitch: Record<string, PitchDims>
  /** Approximations the compiler chose to disclose rather than hide. */
  notes: string[]
  spec: AnalysisSpec
}

export interface ValuesResult {
  property: string
  games: string[]
  values: unknown[]
  /**
   * Only for `property=game`: geometry per match, so the picker can warn that
   * a selection mixes pitch sizes before any query runs. Absent if unreadable.
   */
  pitch?: Record<string, PitchDims>
}

const qs = (pairs: Array<[string, string]>): string =>
  pairs.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&')

export const subjects = (): Promise<RootCatalog> =>
  api<RootCatalog>('/api/v1/semantic/catalog')

export const describe = (subject: string): Promise<SubjectCatalog> =>
  api<SubjectCatalog>(`/api/v1/semantic/catalog?${qs([['subject', subject]])}`)

export const values = (property: string, games: string[]): Promise<ValuesResult> =>
  api<ValuesResult>(
    `/api/v1/semantic/values?${qs([['property', property],
      ...games.map((g) => ['game', g] as [string, string])])}`,
  )

export const analyse = (spec: AnalysisSpec): Promise<AnalysisResult> =>
  api<AnalysisResult>('/api/v1/semantic/analysis', {
    method: 'POST',
    body: JSON.stringify({ spec: orderSpec(spec) }),
  })

/**
 * One match's video source and the calibration frameToVideoTime needs,
 * straight from the game's own record. A match with no attached video is
 * absent from the map, and its rows render without a Watch button.
 */
export interface ExampleVideo {
  game_code: string
  video_url: string
  /** game_files' recorded rate; null when none is recorded (no Watch). */
  fps: number | null
  video_game_start_time: number | null
  video_halftime_start_time: number | null
  video_halftime_end_time: number | null
  video_frame_aligned: boolean
  video_offset_h1_s?: number | null
  video_offset_h2_s?: number | null
  video_time_map?: [number, number][] | null
}

/**
 * The raw subject rows behind one aggregated result row. `columns` are the
 * population's own names (game, frame, receiver, p_x, ...) rather than
 * labelled schema — this is a staff drill-down, not a chart.
 */
export interface ExamplesResult {
  columns: string[]
  rows: unknown[][]
  row_count: number
  /** Which column anchors a video seek (the subject's frame). */
  frame_column: string
  fps: Record<string, number>
  video: Record<string, ExampleVideo>
}

/** The drill-down: `bin` maps a result's breakdown column name to the
 *  clicked row's value (a binned value is its bin's lower edge). */
export const examples = (
  spec: AnalysisSpec, bin: Record<string, unknown>, limit = 50,
): Promise<ExamplesResult> =>
  api<ExamplesResult>('/api/v1/semantic/examples', {
    method: 'POST',
    body: JSON.stringify({ spec: orderSpec(spec), bin, limit }),
  })

/** Column indices by role, in the order the compiler emitted them. */
export const indicesOf = (result: AnalysisResult, role: string): number[] =>
  result.columns.map((c, i) => (c.role === role ? i : -1)).filter((i) => i >= 0)
